// material
import {
  Stack,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Device, CompleteLocation, DeviceParameter } from '../../@types/model/device';
import { RootState, useSelector } from '../../redux/store';
import LoadingSpinner from '../LoadingSpinner';
import {
  DEVICE_TAG_GROUP,
  DEVICE_TAG_LABEL,
  DeviceTag,
  DeviceType,
  DeviceTypeParameter,
  FLOAT_PARAMETER_TYPE,
  INT_PARAMETER_TYPE,
  SELECT_PARAMETER_TYPE,
  STATIC_IMAGE_PARAMETER_TYPE,
  TEXT_PARAMETER_TYPE,
  TEXTAREA_PARAMETER_TYPE,
  DATE_PARAMETER_TYPE,
  Building,
  BOOLEAN_PARAMETER_TYPE
} from '../../@types/model/user';
import React, { Fragment, useEffect, useState } from 'react';
import { CoordinatesPicker } from '../CoordinatesPicker';
import TextParameterField from './DeviceTypeParameterFields/TextParameterField';
import SelectParameterField from './DeviceTypeParameterFields/SelecParameterField';
import { v4 } from 'uuid';
import ImageParameterField from './DeviceTypeParameterFields/ImageParameterField';
import { buildingState, getBuildings } from 'recoil/buildingState';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import DayjsUtils from '@date-io/dayjs';
import dayjs from 'dayjs';
import axios from '../../utils/axios';

// ----------------------------------------------------------------------
interface DeviceFormProps {
  device: Device;
  updateDevice: (device: Device) => void;
  isEdit: boolean;
}

const deviceTypeParameterFieldFactory = (
  dp: DeviceTypeParameter,
  parameter: DeviceParameter,
  changeHandler: (value: any) => void,
  t: (text: string) => string
) => {
  switch (dp.type) {
    case BOOLEAN_PARAMETER_TYPE:
      return (
        <Fragment key={`${dp.type}-${dp.identifier}`}>
          <FormControlLabel
            control={<Checkbox />}
            label={dp.label}
            onChange={(event: React.SyntheticEvent) => {
              // @ts-ignore
              changeHandler(event.target.checked);
            }}
            value={parameter.value !== false}
            checked={parameter.value !== false}
          />
          {dp.label === 'Calibrado' ? (
            <small style={{ marginTop: '-5px', paddingLeft: '10px', color: 'gray' }}>
              {t('sensor_calibration_text')}
            </small>
          ) : null}
        </Fragment>
      );
    case TEXT_PARAMETER_TYPE:
      return (
        <TextParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case TEXTAREA_PARAMETER_TYPE:
      return (
        <TextParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case SELECT_PARAMETER_TYPE:
      return (
        <SelectParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case STATIC_IMAGE_PARAMETER_TYPE:
      return (
        <ImageParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case INT_PARAMETER_TYPE:
      return (
        <TextParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case FLOAT_PARAMETER_TYPE:
      return (
        <TextParameterField
          key={`${dp.type}-${dp.identifier}`}
          parameter={dp}
          value={parameter}
          changeHandler={changeHandler}
        />
      );
    case DATE_PARAMETER_TYPE:
      return (
        <LocalizationProvider
          dateAdapter={DayjsUtils}
          locale={navigator.language.split('-')[0]}
          key={`${dp.type}-${dp.identifier}`}
        >
          <DatePicker
            label={dp.label}
            value={parameter.value}
            onChange={changeHandler}
            renderInput={(params) => {
              params.error = false;
              return <TextField {...params} />;
            }}
          />
        </LocalizationProvider>
      );
    default:
      return null;
  }
};

const DeviceForm = (props: DeviceFormProps) => {
  const { t } = useTranslation(['translation', 'custom']);

  const buildings: Building[] = useRecoilValue(buildingState);
  const setBuildings = useSetRecoilState(buildingState);
  useEffect(() => {
    if (buildings.length === 0) {
      getBuildings().then((buildings: Building[]) => {
        setBuildings(buildings);
      });
    }
  }, []);
  const [deviceTagsAll, setDeviceTagsAll] = useState<DeviceTag[]>([]);
  const [tagsAll, setTagsAll] = useState<string[]>([]);

  const { loaded, deviceTags, deviceTypes } = useSelector((state: RootState) => state.user);
  const { user } = useSelector((state: RootState) => state.user);

  // Check if we have all the deviceTags that have this device. If you are editing a device that not belong to you, it can be necessary to add the tags of the owner.
  useEffect(() => {
    let owner: string = props.device.attributes.owner_guid ?? '';
    if (props.device.attributes.owner_guid) {
      if (props.device.attributes.owner_guid.split(':').length === 3) {
        owner = props.device.attributes.owner_guid.split(':')[2];
      }
    }
    if (owner !== user?.id && props.isEdit) {
      // This device  is a reference to another one. We should take the list of the tags available for that user.
      // Search the tags that should have the selector
      axios.get('/api/v2/device-tag/device/' + props.device.guid).then((data) => {
        let dts = data.data.data.map((dt: any) => dt as DeviceTag);
        setDeviceTagsAll(dts);
        setTagsAll(props.device.attributes.tags);
      });
    } else {
      setDeviceTagsAll(deviceTags);
      setTagsAll(props.device.attributes.tags);
    }
  }, [deviceTags, props.device.attributes.tags]);

  if (!loaded) {
    return <LoadingSpinner />;
  }

  const handleDeviceParameterChange = (
    deviceParameterDefinicion: DeviceTypeParameter,
    value: any
  ) => {
    const fieldExists = props.device.attributes.parameters.find(
      (p: DeviceParameter) => p.identifier === deviceParameterDefinicion.identifier
    );
    if (deviceParameterDefinicion.type === 'date') {
      value = dayjs(value).format('YYYY-MM-DD');
    }
    if (deviceParameterDefinicion.type === 'boolean') {
      value = value === true;
    }
    if (fieldExists) {
      props.updateDevice({
        guid: props.device.guid,
        attributes: {
          ...props.device.attributes,
          parameters: props.device.attributes.parameters.map((deviceParameter: DeviceParameter) => {
            if (deviceParameter.identifier !== deviceParameterDefinicion.identifier) {
              return deviceParameter;
            } else {
              return { ...deviceParameter, value: value } as DeviceParameter;
            }
          })
        }
      } as Device);
    } else {
      props.updateDevice({
        guid: props.device.guid,
        attributes: {
          ...props.device.attributes,
          parameters: [
            ...props.device.attributes.parameters,
            {
              guid: v4(),
              identifier: deviceParameterDefinicion.identifier,
              value: value
            } as DeviceParameter
          ]
        }
      } as Device);
    }
  };

  return (
    <Fragment>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <Stack direction={'column'} width={'50%'} spacing={3}>
          <Typography variant={'h5'}>{t('general_data')}</Typography>
          <FormControl disabled={props.isEdit} fullWidth>
            <InputLabel id={'device-type-select-label'}>{t('select_device_type')}</InputLabel>
            <Select
              labelId={'device-type-select-label'}
              id={'device-type-select'}
              value={props.device.attributes.device_type_preset}
              label={t('select_device_type').toString()}
              onChange={(event) => {
                props.updateDevice({
                  guid: props.device.guid,
                  attributes: {
                    ...props.device.attributes,
                    device_type_preset: event.target.value
                  }
                });
              }}
            >
              {deviceTypes.map((dt: DeviceType) => {
                return (
                  <MenuItem key={dt.guid} value={dt.guid}>
                    {dt.attributes.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="text"
            value={props.device.attributes.identifier}
            disabled={props.isEdit}
            label={t('device_identifier').toString()}
            onChange={(event) =>
              props.updateDevice({
                guid: props.device.guid,
                attributes: { ...props.device.attributes, identifier: event.target.value }
              })
            }
          />
          <TextField
            fullWidth
            type="text"
            value={props.device.attributes.name}
            label={t('name').toString()}
            onChange={(event) =>
              props.updateDevice({
                guid: props.device.guid,
                attributes: { ...props.device.attributes, name: event.target.value }
              })
            }
          />
          <FormControl fullWidth>
            <InputLabel id={'device-state-select-label'}>{t('state')}</InputLabel>
            <Select
              labelId={'device-state-select-labe'}
              id={'device-state-select'}
              value={props.device.attributes.state}
              label={t('state').toString()}
              onChange={(event) => {
                props.updateDevice({
                  guid: props.device.guid,
                  attributes: { ...props.device.attributes, state: event.target.value }
                });
              }}
            >
              <MenuItem value={'created'}>{t('state_inactive')}</MenuItem>
              <MenuItem value={'active'}>{t('state_active')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              id="device-group-picker "
              value={deviceTagsAll
                .filter((dt: DeviceTag) => dt.attributes.type === DEVICE_TAG_GROUP)
                .filter((dt: DeviceTag) => tagsAll.includes(dt.guid))}
              onChange={(event, newValue) => {
                /*setValue([
                  ...fixedOptions,
                  ...newValue.filter((option) => fixedOptions.indexOf(option) === -1),
                ]);*/
                let previousTagsNotDeviceGroup = tagsAll.filter((tag: string) => {
                  let totalBuildingsFiltered = deviceTagsAll.filter((dt: DeviceTag) => {
                    return dt.guid === tag;
                  }).length;
                  if (totalBuildingsFiltered > 0) return false;
                  return true;
                });
                let newTags = [
                  ...newValue.map((i: DeviceTag) => i.guid),
                  ...previousTagsNotDeviceGroup
                ];
                props.updateDevice({
                  guid: props.device.guid,
                  attributes: {
                    ...props.device.attributes,
                    tags: newTags
                  }
                });
              }}
              options={deviceTagsAll.filter(
                (dt: DeviceTag) => dt.attributes.type === DEVICE_TAG_GROUP
              )}
              getOptionLabel={(option) => option.attributes.name}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.guid}>
                    {option.attributes.name}
                  </li>
                );
              }}
              renderTags={(tagValue, getTagProps) => {
                return tagValue.map((option, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <Chip label={option.attributes.name} {...getTagProps({ index })} />
                ));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t('device_groups', { ns: 'custom' }).toString()}
                  placeholder={t('device_groups_placeholder').toString()}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t('building')}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tagsAll.find((tag: string) => {
                return buildings.filter((b: Building) => {
                  return b.guid === tag;
                }).length;
              })}
              label="Name"
              onChange={(event) => {
                // We only admit one deviceTag type asset. We should delete first the previous deviceTag
                let tagsThatAreNotBuildings = tagsAll.filter((tag: string) => {
                  let totalBuildingsFiltered = buildings.filter((b: Building) => {
                    return b.guid === tag;
                  }).length;
                  if (totalBuildingsFiltered > 0) return false;
                  return true;
                });
                props.updateDevice({
                  guid: props.device.guid,
                  attributes: {
                    ...props.device.attributes,
                    tags: [...tagsThatAreNotBuildings, event.target.value]
                  }
                });
              }}
            >
              {buildings.map((b: Building) => {
                return (
                  <MenuItem key={b.guid} value={b.guid}>
                    {b.attributes.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              id="device-label-picker"
              value={deviceTagsAll
                .filter((dt: DeviceTag) => dt.attributes.type === DEVICE_TAG_LABEL)
                .filter((dt: DeviceTag) => tagsAll.includes(dt.guid))}
              onChange={(event, newValue) => {
                /*setValue([
                  ...fixedOptions,
                  ...newValue.filter((option) => fixedOptions.indexOf(option) === -1),
                ]);*/
                props.updateDevice({
                  guid: props.device.guid,
                  attributes: {
                    ...props.device.attributes,
                    tags: newValue.map((i: DeviceTag) => i.guid)
                  }
                });
              }}
              options={deviceTagsAll.filter(
                (dt: DeviceTag) => dt.attributes.type === DEVICE_TAG_LABEL
              )}
              getOptionLabel={(option) => option.attributes.name}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.guid}>
                    {option.attributes.name}
                  </li>
                );
              }}
              renderTags={(tagValue, getTagProps) => {
                return tagValue.map((option, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <Chip label={option.attributes.name} {...getTagProps({ index })} />
                ));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t('tags').toString()}
                  placeholder={t('device_groups_placeholder').toString()}
                />
              )}
            />
          </FormControl>
        </Stack>
        <Stack direction={'column'} width={'50%'}>
          <Typography variant={'h5'} sx={{ marginBottom: 3 }}>
            {t('device_location')}
          </Typography>
          <CoordinatesPicker
            onClick={(data: CompleteLocation) => {
              //setFieldValue('location', data);
              props.updateDevice({
                guid: props.device.guid,
                attributes: { ...props.device.attributes, last_location: data }
              });
            }}
            onRemove={() => {
              props.updateDevice({
                guid: props.device.guid,
                attributes: { ...props.device.attributes, last_location: null }
              });
            }}
            marker={
              props.device.attributes.last_location ? props.device.attributes.last_location : null
            }
            height={'300px'}
            editionEnabled
            showSearchBox
          />
        </Stack>
      </Stack>
      <Stack direction={'column'} spacing={3} mt={3}>
        <Typography variant={'h5'} sx={{ marginBottom: 0 }}>
          {t('device_properties')}
        </Typography>
        {props.device.attributes.device_type_preset !== ''
          ? deviceTypes
              .find((dt: DeviceType) => dt.guid === props.device.attributes.device_type_preset)!
              .attributes.parameter_definitions.filter((pd: DeviceTypeParameter) => {
                // We should delete from metrosilo the params "Numero de crianza" and "Tipo de producto"
                if (props.device.attributes.device_type_preset === 'preset:metrosil-v1') {
                  if (['numero_de_crianza', 'tipo_de_producto'].includes(pd.identifier))
                    return false;
                }
                return true;
              })
              .map((pd: DeviceTypeParameter) => {
                const deviceParameter = props.device.attributes.parameters.find(
                  (p: DeviceParameter) => p.identifier === pd.identifier
                );
                return deviceTypeParameterFieldFactory(
                  pd,
                  deviceParameter
                    ? deviceParameter
                    : ({
                        identifier: pd.identifier,
                        guid: v4(),
                        value: ''
                      } as DeviceParameter),
                  (data: any) => {
                    handleDeviceParameterChange(pd, data);
                  },
                  t
                );
              })
          : null}
      </Stack>
    </Fragment>
  );
};

export default DeviceForm;
